// "Now & next", the top of the agents list (NowNext): which agents are working, queued, waiting for a
// decision, being checked, and which run next. Built from the list's own answer (GET /api/agents/:
// each agent's `activity`, `pending_cards` and `next_run_at`), so it costs no read of its own.
//
// Pure: no React, and no clock or zone of its own; the caller passes `now` and the viewer's time zone.
//
// The groups, in this order (docs/superpowers/specs/2026-10-15-agent-activity-design.md):
// 1. working: an agent with a running run: the run's newest step, whatever its status (the last thing
//    the run did, shown while the model thinks between actions), or "Starting..." before its first
//    step (and for a viewer who may not see the agent's history: the server leaves the step out);
// 2. queued: an agent with runs queued and none running;
// 3. waiting: an agent whose cards wait for a decision (pending_cards): "N to approve". A halted
//    agent's cards can't be decided until it runs again, so its row says its status too, as its list
//    row does ("Needs you", a middle dot, "paused");
// 4. checking: an agent whose cards NotesAI is checking: "checking N";
// 5. next: the 3 soonest next runs of the active agents, in words ("today 2:00 PM"). A watch says
//    until when it watches instead ("watching until 5:00 PM").
// An agent whose activity couldn't be read (null: one of the server's reads failed) is in none of the
// groups that come from it (working, queued, checking); its waiting cards and its next run are the
// list's own fields, and still show. Only the groups that have rows are returned, so none at all
// means there is nothing to show.
//
// Time words, in the viewer's zone: "today 2:00 PM", "tomorrow 9:00 AM", "Thu 9:00 AM" up to six
// days on, else "Oct 22, 9:00 AM" (a time already gone included, so a weekday never reads as the
// coming one). Days are counted on the calendar, not in 24-hour steps, so the night the clocks change
// is still one night.
//
// Shared with the rest of the agents UI: HALTED, and the running and queued rule (runningRun,
// isQueued), which useAgents' poll uses too, so the pace follows what the section shows.

// The characters the words use that aren't ASCII, so this file stays ASCII.
const ELLIPSIS = String.fromCharCode(0x2026);
const DOT = String.fromCharCode(0xb7);

export const STARTING = `Starting${ELLIPSIS}`;
// How many next runs "Next up" shows.
export const NEXT_SHOWN = 3;
// The statuses in which an agent takes no new run (types.NO_NEW_RUNS): its cards can't be decided.
export const HALTED = new Set(['paused', 'suspended', 'archived']);
const GROUPS = [
  ['working', 'Working now'],
  ['queued', 'Queued'],
  ['waiting', 'Waiting for you'],
  ['checking', 'Being checked'],
  ['next', 'Next up'],
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_MS = 24 * 60 * 60 * 1000;
const FORMAT = {
  weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
};

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
// A count above zero, else 0 (a missing, unread or odd count shows nothing).
const count = (value) => (Number.isInteger(value) && value > 0 ? value : 0);
const titleOf = (agent) => (typeof agent.title === 'string' && agent.title.trim() ? agent.title : 'Agent');
const activityOf = (agent) => (isObject(agent) && isObject(agent.activity) ? agent.activity : null);

// The agent's running run ({run_id, started_at, step}), or null: none, or activity that couldn't be
// read. It is "Working now".
export function runningRun(agent) {
  const activity = activityOf(agent);
  return activity && isObject(activity.running) ? activity.running : null;
}

// Whether the agent has runs queued and none running: "Queued" (a run going is "Working now").
export function isQueued(agent) {
  const activity = activityOf(agent);
  return !!activity && !runningRun(agent) && count(activity.queued) > 0;
}

// An ISO time from the API as a Date, or null.
function readTime(value) {
  if (typeof value !== 'string' || !value) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

// `now` as a Date: a Date or a number of milliseconds; anything else is the clock's now.
function readNow(now) {
  const at = now instanceof Date ? now : new Date(typeof now === 'number' ? now : Date.now());
  return Number.isNaN(at.getTime()) ? new Date() : at;
}

// One formatter per zone (building one is slow). A zone this browser doesn't know is its own zone.
const formats = new Map();
function formatFor(timeZone) {
  const key = timeZone || '';
  if (!formats.has(key)) {
    let format;
    try {
      format = new Intl.DateTimeFormat('en-US', { ...FORMAT, timeZone: timeZone || undefined });
    } catch {
      format = new Intl.DateTimeFormat('en-US', FORMAT);
    }
    formats.set(key, format);
  }
  return formats.get(key);
}

// The clock and calendar in `timeZone` at `date`: {weekday, year, month, day, hour, minute, dayPeriod}.
// Read part by part, so the words are put together here, with plain spaces (a browser may put a
// narrow no-break space before "PM").
function partsOf(date, timeZone) {
  const parts = {};
  formatFor(timeZone).formatToParts(date).forEach(({ type, value }) => { parts[type] = value; });
  return parts;
}

const dayNumber = (parts) => Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)) / DAY_MS;

// When `at` is, for a viewer in `timeZone` at `now`: {days (calendar days from today), day (its
// words: "today", "tomorrow", "Thu" or "Oct 22,"), time ("2:00 PM")}.
function when(at, now, timeZone) {
  const there = partsOf(at, timeZone);
  const days = Math.round(dayNumber(there) - dayNumber(partsOf(now, timeZone)));
  let day = `${MONTHS[Number(there.month) - 1]} ${Number(there.day)},`;
  if (days === 0) day = 'today';
  else if (days === 1) day = 'tomorrow';
  else if (days > 1 && days < 7) day = there.weekday;
  return { days, day, time: `${there.hour}:${there.minute} ${there.dayPeriod}` };
}

function words(at, now, timeZone) {
  const { day, time } = when(at, now, timeZone);
  return `${day} ${time}`;
}

// An ISO time in words for a viewer in `timeZone` at `now`; null for a time that can't be read.
export function timeWords(iso, now, timeZone) {
  const at = readTime(iso);
  return at ? words(at, readNow(now), timeZone) : null;
}

// A watch's row: until when it watches, the time alone when that is today.
function watchWords(until, now, timeZone) {
  const { days, day, time } = when(until, now, timeZone);
  return `watching until ${days === 0 ? time : `${day} ${time}`}`;
}

// The groups for `agents` (the list's answer), at `now` (a Date or milliseconds; the clock's when
// there is none) in `timeZone` (the viewer's own when there is none): [{key, title, rows}], each row
// {agentId, title, detail, halted}, in the spec's order, only the groups that have rows.
export function nowNextRows(agents, now, timeZone) {
  const clock = readNow(now);
  const list = Array.isArray(agents) ? agents.filter((agent) => isObject(agent) && agent.id != null && agent.id !== '') : [];
  const rows = new Map(GROUPS.map(([key]) => [key, []]));
  const add = (key, agent, detail) => rows.get(key).push({
    agentId: agent.id, title: titleOf(agent), detail, halted: HALTED.has(agent.status),
  });

  list.forEach((agent) => {
    const run = runningRun(agent);
    if (run) add('working', agent, typeof run.step === 'string' && run.step.trim() ? run.step : STARTING);
    else if (isQueued(agent)) add('queued', agent, 'queued');
    const pending = count(agent.pending_cards);
    if (pending) add('waiting', agent, HALTED.has(agent.status) ? `${pending} to approve ${DOT} ${agent.status}` : `${pending} to approve`);
    const checking = count(activityOf(agent)?.checking);
    if (checking) add('checking', agent, `checking ${checking}`);
  });

  // Soonest first; agents due at the same time keep the list's order (sort is stable).
  list
    .filter((agent) => agent.status === 'active')
    .map((agent) => ({ agent, at: readTime(agent.next_run_at) }))
    .filter(({ at }) => at)
    .sort((a, b) => a.at - b.at)
    .slice(0, NEXT_SHOWN)
    .forEach(({ agent, at }) => {
      const until = readTime(activityOf(agent)?.watch_until);
      add('next', agent, until ? watchWords(until, clock, timeZone) : words(at, clock, timeZone));
    });

  return GROUPS.filter(([key]) => rows.get(key).length).map(([key, title]) => ({ key, title, rows: rows.get(key) }));
}
